// Product page detail rows (phone 2026-11-01, desktop 2026-11-01): Description, Full Specifications, Reviews, Glossary,
// Shipping & Returns. Every row opens by default; tapping / clicking a head opens or closes it with a calm slide.
// Phones: the open row's head sticks under the header (CSS, fz-ui.css); tapping a PINNED head brings that row back to its
// start, still open. Desktop: the left "On this page" nav ([data-pdp-toc-list]) lists every row, marks the one being read
// and opens a closed row it jumps to.
// Rows: [data-pdp-fold] (a <details> or a section) > .pdp-fold__head + [data-pdp-fold-body].
(() => {
  const phone = matchMedia('(max-width: 47.99rem)');
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const folds = () => [...document.querySelectorAll('[data-pdp-fold]')];
  const isOpen = (f) => f.hasAttribute('data-expanded');
  const header = () => document.querySelector('.fz-site-header');
  // Where a pinned head sits now: under the header when it shows, at the top edge while it is hidden on scroll.
  const pinTop = () => (document.documentElement.hasAttribute('data-hdr-hidden') ? 0 : header()?.offsetHeight || 0);

  const set = (fold, open) => {
    fold.toggleAttribute('data-expanded', open);
    fold.toggleAttribute('data-closed', !open);
    if (fold.tagName === 'DETAILS') fold.open = open;
    fold.querySelector('.pdp-fold__head')?.setAttribute('aria-expanded', String(open));
  };

  const running = new WeakMap();
  const toggle = (fold, opening) => {
    const body = fold.querySelector('[data-pdp-fold-body]');
    running.get(fold)?.cancel();
    if (!body || reduce()) { set(fold, opening); return; }
    // The row's bottom spacing is a pseudo-element inside the body (fz-ui.css), so the height runs cleanly to 0.
    let anim;
    if (opening) {
      set(fold, true);
      anim = body.animate([{ height: '0px', opacity: 0.2 }, { height: `${body.scrollHeight}px`, opacity: 1 }], { duration: phone.matches ? 360 : 600, easing: EASE });
    } else {
      fold.removeAttribute('data-expanded');
      anim = body.animate([{ height: `${body.offsetHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: phone.matches ? 360 : 600, easing: EASE });
      anim.onfinish = () => set(fold, false);
    }
    // Clip only while sliding (desktop rows stay unclipped for hover washes and Judge.me popups).
    body.style.overflow = 'hidden';
    const done = () => { body.style.overflow = ''; };
    anim.addEventListener('finish', done);
    anim.addEventListener('cancel', done);
    running.set(fold, anim);
  };

  const init = () => {
    folds().forEach((fold) => {
      if (fold.dataset.pdpFoldReady !== undefined) return;
      fold.dataset.pdpFoldReady = '';
      set(fold, true);
      // Opened from elsewhere (e.g. a spec link scrolling to a Glossary term): keep the icon in step.
      if (fold.tagName === 'DETAILS') fold.addEventListener('toggle', () => { fold.toggleAttribute('data-expanded', fold.open); fold.toggleAttribute('data-closed', !fold.open); });
    });
  };

  document.addEventListener('click', (e) => {
    const head = e.target.closest?.('.pdp-fold__head');
    const fold = head?.closest('[data-pdp-fold]');
    if (!fold || e.target.closest('a') || fold.closest('[data-quick-add-content]')) return;
    e.preventDefault();
    const top = fold.getBoundingClientRect().top;
    // Phones, pinned head: back to the row's start, open. Scrolling up brings the header back, so land right under it.
    if (phone.matches && isOpen(fold) && top < pinTop() - 2) {
      const target = window.scrollY + top - (header()?.offsetHeight || 0);
      window.scrollTo({ top: target, behavior: reduce() ? 'instant' : 'smooth' });
      return;
    }
    toggle(fold, !isOpen(fold));
  });

  // Desktop "On this page": one link per row, same look as the article page.
  const list = document.querySelector('[data-pdp-toc-list]');
  const links = new Map();
  const buildToc = () => {
    if (!list || list.childElementCount) return;
    for (const fold of folds()) {
      if (!fold.id || fold.closest('[data-quick-add-content]')) continue;
      const title = fold.querySelector('.pdp-fold__head h3, .pdp-fold__head > span:first-child')?.textContent.trim();
      if (!title) continue;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${fold.id}`;
      link.textContent = title;
      link.className = list.dataset.linkClass || '';
      link.addEventListener('click', (ev) => {
        ev.preventDefault();
        if (!isOpen(fold)) set(fold, true);
        const y = window.scrollY + fold.getBoundingClientRect().top - (header()?.offsetHeight || 0) - 24;
        window.scrollTo({ top: y, behavior: reduce() ? 'instant' : 'smooth' });
        history.replaceState(null, '', `#${fold.id}`);
      });
      item.append(link);
      list.append(item);
      links.set(fold, link);
    }
    if (links.size < 2) list.closest('.pdp-toc')?.setAttribute('hidden', '');
  };

  const markCurrent = () => {
    if (!links.size) return;
    const line = (header()?.offsetHeight || 0) + 80;
    let current = links.keys().next().value;
    for (const fold of links.keys()) if (fold.getBoundingClientRect().top - line <= 0) current = fold;
    for (const [fold, link] of links) {
      if (fold === current) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  };

  // Phones: a pinned head gets a hairline under it (it floats over the text). Desktop: the nav follows the reading.
  let ticking = false;
  const onScroll = () => {
    ticking = false;
    if (!phone.matches) { markCurrent(); return; }
    const t = pinTop();
    folds().forEach((fold) => {
      const r = fold.getBoundingClientRect();
      fold.toggleAttribute('data-stuck', isOpen(fold) && r.top < t - 2 && r.bottom > t + 56);
    });
  };
  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });

  init();
  buildToc();
  markCurrent();
  phone.addEventListener('change', init);
})();
